import { useMemo, useState } from "react";
import LiteralQuestionAdmin from "../../feature/admin/LiteralQuestionAdmin";
import Button from "react-bootstrap/Button";
import Spinner from "../../feature/Spinner";
import TextInput from "../../feature/TextInput";
import Alert from "react-bootstrap/Alert";
import Translated from "../../feature/Translated";
import DropdownInput from "../../feature/DropdownInput";
import { useListLiteralQuestions } from "../../../api-new";
import { useTranslation } from "../../feature/TranslationProvider";
import { LICENSE_CATEGORY_TO_LOCALIZATION_KEY } from "../../../data/licenseCategories";
import QuestionModalLink from "../../feature/QuestionModalLink";

const SORTING_VALUES = [
  [null, "none"],
  ["reports", "reports"],
  ["bookRefs", "book_references"],
  ['needsReview', 'needs_review'],
  ['needsLocalization', 'needs_localization']
];

const LANGUAGES = Object.keys(LANGUAGE_SELECTIONS).map((lang) => [
  lang,
  LANGUAGE_SELECTIONS[lang],
]);

const STATUS_VALUES = [
  ['active', 'active'],
  ['inactive', 'inactive'],
  [null, '*'],
];

const LICENSE_CATEGORIES = [
  ...LICENSE_CATEGORY_TO_LOCALIZATION_KEY,
  [null, '*']
];

const reviewValues = [
  [null, '*'],
  ['needs_review', 'needs_review'],
  ['no_review_needed', 'no_review_needed'],
];

const PRESELECTED_LICENSE_CATEGORY = 'b';
const PRESELECTED_LANGUAGE = 'fi';
const PRESELECTED_STATUS = 'active';
const PRESELECTED_SORTING_VALUE = null;
const PRESELECTED_REVIEW_VALUE = null;

const AdminLiteralsPage = () => {
  const { translate } = useTranslation();
  const [language, setLanguage] = useState(PRESELECTED_LANGUAGE);
  const [category, setCategory] = useState(PRESELECTED_LICENSE_CATEGORY);
  const [status, setStatus] = useState(PRESELECTED_STATUS);
  const [sortBy, setSortBy] = useState(PRESELECTED_SORTING_VALUE);
  const [review, setReview] = useState(PRESELECTED_REVIEW_VALUE);
  const [id, setId] = useState("");
  const [queryInputs, setQueryInputs] = useState({ language, category, id, status, review });
  const [searchText, setSearchText] = useState("");
  const literalsQuery = useListLiteralQuestions(
    queryInputs.category,
    queryInputs.language,
    queryInputs.id,
    queryInputs.status,
    queryInputs.review
  );

  const filterListener = (filter, value) => {
    switch (filter) {
      case "language":
        setLanguage(value);
        break;
      case "category":
        setCategory(value);
        break;
      case "id":
        setId(value);
        break;
      case "status":
        setStatus(value);
        break;
      case "review":
        setReview(value);
        break;
      case "search":
        setSearchText(value);
        break;
      default:
        break;
    }
  };

  const initFilters = () => {
    setQueryInputs({ language, category, id, status, review });
  };

  const sortListener = (value) => {
    setSortBy(value);
  };

  const sortQuestionsOnId = (question1, question2) => {
    const id1 = question1.id;
    const id2 = question2.id;
    if (id1 === id2) {
      return 0;
    }
    return id1 > id2 ? 1 : -1;
  };

  const sortQuestionsOnReportsDesc = (question1, question2) => {
    const len1 = question1.reports ? question1.reports.length : 0;
    const len2 = question2.reports ? question2.reports.length : 0;
    if (len1 === len2) {
      return 0;
    }
    return len1 > len2 ? -1 : 1;
  };

  const sortQuestionsOnBookRefs = (question1, question2) => {
    const id1 = question1.bookReferenceIds
      ? question1.bookReferenceIds[0]
      : null;
    const id2 = question2.bookReferenceIds
      ? question2.bookReferenceIds[0]
      : null;
    if (id1 === id2) {
      return 0;
    }
    return id1 > id2 ? 1 : -1;
  };

  const sortQuestionsByReviewStatus = (question1, question2) => {
    const review1 = question1.needsReview ? 1 : 0;
    const review2 = question2.needsReview ? 1 : 0;
    if (review1 === review2) {
      return 0;
    }
    return review1 > review2 ? -1 : 1;
  };

  const sortQuestionsByLocalizationStatus = (question1, question2) => +question2.needsLocalization - +question1.needsLocalization;

  const renderLiterals = useMemo(() => {
    if (literalsQuery.isFetching) {
      return <Spinner />;
    }
    if (literalsQuery.isError) {
      return (
        <div>
          <Alert>
            <Translated translationKey={literalsQuery.error.message} />
          </Alert>
        </div>
      );
    }
    const allQuestions = literalsQuery.data?.questions ?? [];
    // Create a copy of the all questions so Array.sort(...) calls do not modify the questions cached by tanstack query directly.
    let questions = [...allQuestions];

    if (!questions) {
      return;
    }

    if (searchText.length > 2) {
      const lowerSearchText = searchText.toLowerCase();
      questions = questions.filter((q) => {
        return q.question.toLowerCase().includes(lowerSearchText);
      });
    }

    if (!questions) {
      return;
    }

    switch (sortBy) {
      case "reports":
        questions.sort(sortQuestionsOnReportsDesc);
        break;
      case "bookRefs":
        questions.sort(sortQuestionsOnBookRefs);
        break;
      case "needsReview":
        questions.sort(sortQuestionsByReviewStatus);
        break;
      case "needsLocalization":
        questions.sort(sortQuestionsByLocalizationStatus);
        break;
      default:
        questions.sort(sortQuestionsOnId);
    }

    return questions.map((literal) => (
      <LiteralQuestionAdmin
        key={literal.id}
        updateId={literal.updateId}
        lang={language}
        question={literal}
        category={category}
        refetchQuestions={literalsQuery.refetch}
      />
    ));
  }, [
    literalsQuery.data,
    literalsQuery.isFetching,
    literalsQuery.isError,
    literalsQuery.error,
    literalsQuery.refetch,
    sortBy,
    language,
    category,
    searchText
  ]);

  return (
    <div className="page-container page-gutter max-width-container">
      <DropdownInput
        listener={(value) => filterListener("language", value)}
        value={language}
        title="language"
        items={LANGUAGES}
      />
      <DropdownInput
        listener={(value) => filterListener("category", value)}
        value={category}
        title="category"
        items={LICENSE_CATEGORIES}
      />
      <DropdownInput
        listener={(value) => filterListener("status", value)}
        value={status}
        title="status"
        items={STATUS_VALUES}
      />
      <DropdownInput
        listener={(value) => filterListener("review", value)}
        value={review}
        title="review_status"
        items={reviewValues}
      />
      <TextInput
        listener={(value) => filterListener("id", value)}
        value={id}
        placeholder="id"
        autoComplete="off"
      />
      <Button id="list-literals-button" onClick={initFilters}>
        {translate("search_do")}
      </Button>
      <hr className="my-3"></hr>
      <DropdownInput
        listener={(value) => sortListener(value)}
        value={sortBy}
        title="sorting"
        items={SORTING_VALUES}
      />
      <TextInput
        listener={(value) => filterListener("search", value)}
        value={searchText}
        placeholder="search_questions"
        autoComplete="off"
      />
       <QuestionModalLink questionId={null} refetch={literalsQuery.refetch}/>
      {renderLiterals}
    </div>
  );
};

export default AdminLiteralsPage;
